/**
 * Nearby (proximity) connect timing — signup-proximity-reliability.
 *
 * Nearby connects used to write no connection_timings row, so "it felt a
 * little slower" (Mark, 2026-10-08) couldn't be measured. This keeps the
 * moments that matter, in ms from when the QR screen opened and asked for a
 * GPS fix (T0):
 *   gps_ready      — the fix arrived (the first suspect for a slow start)
 *   nearby_started — the phone began checking in for people nearby
 *   nearby_seen    — the first person shown on the card
 *   nearby_tap     — this person tapped Connect
 *   nearby_matched — the server reported the mutual match
 *   nearby_woke    — the phone came back in front after a lock (first time)
 *   nearby_back    — its first check-in after that was answered
 *                    ({ ms, failed }: check-ins that failed or gave up first)
 * When the exchange lands (either side), `handOffNearbyTiming(room, label)`
 * opens a regular connection-timing session with the same T0 and these
 * marks; the chat page's mount adds `chat_page_mounted` and writes the row
 * (the existing rail — the dashboard's Timing view shows it). In the new
 * experience the landing is new People, which adds `people_landed` instead.
 */
import { startTiming, markAt } from '../connectionTiming';

let t0 = null;
let marks = {};

/** The QR screen opened and asked for a fix — T0 (a new visit). */
export const beginNearbyTiming = (now = performance.now()) => {
  t0 = now;
  marks = {};
};

/** Start the clock only if this visit hasn't (a mid-visit location grant). */
export const ensureNearbyTiming = (now = performance.now()) => {
  if (t0 === null) beginNearbyTiming(now);
};

/** Record a moment once (the first time it happens this visit), with optional extras. */
export const noteNearby = (name, now = performance.now(), extra) => {
  if (t0 === null || marks[name] !== undefined) return;
  const ms = Math.round(now - t0);
  marks[name] = extra ? { ms, ...extra } : ms;
};

/** Forget the visit (QR page closed / reset). */
export const endNearbyTiming = () => {
  t0 = null;
  marks = {};
};

/** The exchange landed: hand the nearby moments to a timing session for the room. */
export const handOffNearbyTiming = (chatRoomId, label) => {
  if (t0 === null || !chatRoomId) return false;
  startTiming(chatRoomId, 'profile', label, { t0Perf: t0 });
  Object.entries(marks).forEach(([name, mark]) =>
    typeof mark === 'object' ? markAt(name, mark.ms, mark) : markAt(name, mark)
  );
  endNearbyTiming();
  return true;
};

/** Test seam. */
export const nearbyTimingMarks = () => ({ ...marks });
